import { FilterDescriptor } from '@progress/kendo-data-query';
import i18n from 'i18next';
import moment, { Moment } from 'moment/moment';
import {
    AdvanceSelectFilter,
    CategoryType,
    ChipData,
    FieldDefinitionFieldTypeNumeric,
    FilterCategory,
    FilterCategoryItems,
    FilterUIStateUpdate,
    FilterValue,
    LogicOperator,
    Operator,
    PartialDateTimeIgnoreNumeric,
    SelectValue,
} from './interfaces';

const FLOAT_TOLERANCE = 0.0000000000000001;

export const getExpansionPanelTitle = (type: CategoryType) => {
    switch (type) {
        case CategoryType.Risk:
            return i18n.t('admin:RISK');
        default:
            break;
    }
};

export const calculateDefaultFilters = (filterCategories: FilterCategory[]) => {
    const filters: FilterDescriptor[] = [];
    filterCategories.forEach((filterCategory) => {
        filterCategory.items.forEach((category) => {
            category.defaultUncheckedIds?.forEach((value) => {
                filters.push({
                    field: category.valueKey ? `${category.fieldKey}.${category.valueKey}` : category.fieldKey,
                    operator: 'neq',
                    value,
                });
            });
        });
    });
    return filters;
};

export const MapCalculateFiltersToChips = (
    filteredFields: FilterUIStateUpdate[],
    categories: Array<FilterCategory>,
) => {
    if (!filteredFields?.length) return [];

    return filteredFields
        .flatMap<ChipData>((x) => {
            const parentCat = categories.find((c) => c.id === x.parentId);
            const category = parentCat?.items.find((c) => c.fieldKey === x.fieldKey && c.valueKey === x.valueKey);

            if (x.parentId == null || !parentCat || !category) {
                window.console.error('Unsupported filter UI category type');
                return [];
            }

            return [
                {
                    parentCategory: parentCat.id,
                    parentName: getExpansionPanelTitle(parentCat.id) ?? '',
                    category: category.label,
                    categoryId: category.id,
                    index: category.index,
                },
            ];
        })
        .sort((a, b) => {
            if (a.parentCategory === b.parentCategory) return a.index - b.index;
            return a.parentCategory - b.parentCategory;
        });
};

const ItemMatchesNullFilter = (op: Operator.Null | Operator.NotNull, value: FilterValue) => {
    if (op === Operator.Null) return value.value == null;

    return value.value !== null;
};

const BoolItemMatchesFilter = (op: Operator, filter: boolean, value: FilterValue): boolean => {
    if (value.value != null && typeof value.value !== 'boolean') return false;

    const primitive = value.value as boolean | null;

    switch (op) {
        case Operator.Equals:
            return primitive === filter;
        case Operator.NotEqual:
            return primitive !== filter;
        default:
            return false; // Shouldn't ever get here
    }
};

const DateTimeItemMatchesFilter = (op: Operator, filter: Moment, value: FilterValue): boolean => {
    if (value.value != null && typeof value.value !== 'string') return false; // Expect ISO string

    const isNull = value.value == null;

    if (isNull) {
        switch (op) {
            case Operator.Equals:
            case Operator.GreaterThan:
            case Operator.GreaterThanOrEqualTo:
                return false;
            case Operator.NotEqual:
            case Operator.LessThan:
            case Operator.LessThanOrEqualTo:
                return true;
            default:
                return false; // Shouldn't ever get here
        }
    }

    const primitive = moment(value.value);
    if (!primitive.isValid() && !isNull) return false;

    let dateUnit: moment.unitOfTime.StartOf | undefined;
    switch (value.dateTimeIgnore) {
        case PartialDateTimeIgnoreNumeric.IgnoreDay:
            dateUnit = 'month';
            break;
        case PartialDateTimeIgnoreNumeric.IgnoreTime:
            dateUnit = 'day';
            break;
        default:
            dateUnit = undefined;
            break;
    }

    switch (op) {
        case Operator.Equals:
            return primitive.isSame(filter, dateUnit);
        case Operator.NotEqual:
            return !primitive.isSame(filter, dateUnit);
        case Operator.GreaterThan:
            return primitive.isAfter(filter, dateUnit);
        case Operator.GreaterThanOrEqualTo:
            return primitive.isSameOrAfter(filter, dateUnit);
        case Operator.LessThan:
            return primitive.isBefore(filter, dateUnit);
        case Operator.LessThanOrEqualTo:
            return primitive.isSameOrBefore(filter, dateUnit);
        default:
            return false; // Shouldn't ever get here
    }
};

const IntegerItemMatchesFilter = (op: Operator, filter: number, value: FilterValue): boolean => {
    if (value.value != null && Number.isNaN(value.value as number)) return false;

    const primitive = value.value as number | null;
    const isNull = primitive == null;

    switch (op) {
        case Operator.Equals:
            return primitive === filter;
        case Operator.NotEqual:
            return primitive !== filter;
        case Operator.GreaterThan:
            return !isNull && primitive > filter;
        case Operator.GreaterThanOrEqualTo:
            return !isNull && primitive >= filter;
        case Operator.LessThan:
            return isNull || primitive < filter;
        case Operator.LessThanOrEqualTo:
            return isNull || primitive <= filter;
        default:
            return false; // Shouldn't ever get here
    }
};

const FloatItemMatchesFilter = (op: Operator, filter: number, value: FilterValue): boolean => {
    if (value.value != null && Number.isNaN(value.value as number)) return false;

    const primitive = value.value as number | null;
    const isNull = primitive == null;

    switch (op) {
        case Operator.Equals:
            return !isNull && Math.abs(primitive - filter) < FLOAT_TOLERANCE;
        case Operator.NotEqual:
            return isNull || Math.abs(primitive - filter) > FLOAT_TOLERANCE;
        case Operator.GreaterThan:
            return !isNull && primitive > filter;
        case Operator.GreaterThanOrEqualTo:
            return !isNull && (primitive > filter || Math.abs(primitive - filter) < FLOAT_TOLERANCE);
        case Operator.LessThan:
            return isNull || primitive < filter;
        case Operator.LessThanOrEqualTo:
            return isNull || primitive < filter || Math.abs(primitive - filter) < FLOAT_TOLERANCE;
        default:
            return false; // Shouldn't ever get here
    }
};

const StringItemMatchesFilter = (op: Operator, filter: string, value: FilterValue): boolean => {
    if (value.value != null && typeof value.value !== 'string') return false;

    const primitive = value.value?.toLocaleLowerCase();
    const comparisonValue = filter.toLocaleLowerCase();
    const isNull = primitive == null;

    switch (op) {
        case Operator.Equals:
            return primitive === comparisonValue;
        case Operator.NotEqual:
            return primitive !== comparisonValue;
        case Operator.Contains:
            return !isNull && primitive.indexOf(comparisonValue) > -1;
        case Operator.NotContains:
            return isNull || primitive.indexOf(comparisonValue) < 0;
        default:
            return false;
    }
};

const ProcessFilter = (filter: SelectValue, value: FilterValue) => {
    let matches = false;
    if (filter.operator == null) return true;

    if (filter.operator === Operator.Null || filter.operator === Operator.NotNull) {
        matches = ItemMatchesNullFilter(filter.operator, value);
    } else {
        // Expected to use the above Null/NotNull operators. Return false if value is null
        if (filter.value == null) return false;
        switch (filter.dataType) {
            case FieldDefinitionFieldTypeNumeric.Bool:
                matches = BoolItemMatchesFilter(filter.operator, filter.value, value);
                break;
            case FieldDefinitionFieldTypeNumeric.Date:
            case FieldDefinitionFieldTypeNumeric.PartialDate:
                matches = DateTimeItemMatchesFilter(filter.operator, filter.value as Moment, value);
                break;
            case FieldDefinitionFieldTypeNumeric.Int:
                matches = IntegerItemMatchesFilter(filter.operator, filter.value, value);

                break;
            case FieldDefinitionFieldTypeNumeric.Float:
                matches = FloatItemMatchesFilter(filter.operator, filter.value, value);

                break;
            case FieldDefinitionFieldTypeNumeric.Text:
            case FieldDefinitionFieldTypeNumeric.CSV:
                matches = StringItemMatchesFilter(filter.operator, filter.value, value);
                break;
            default:
                break;
        }
    }

    return matches;
};

export const ItemMatchesFilter = (value: FilterValue, filters: AdvanceSelectFilter): boolean => {
    if (filters.filters.length === 0) return true;

    if (filters.logic === LogicOperator.AND) {
        return filters.filters.every((filter) => {
            return ProcessFilter(filter, value);
        });
    }
    return filters.filters.some((filter) => {
        return ProcessFilter(filter, value);
    });
};

export const commaSplitCompare = (rowValue: string, exclusionList: string[]) => {
    if (exclusionList == null) return false;

    const exclusions = new Set<string | null>(...exclusionList);

    const filtered = String(rowValue)
        .split(',')
        .map((item) => item.trim())
        .filter((item) => (item === '' ? !exclusions.has(null) : !exclusions.has(item)));

    return filtered.length > 0;
};

export const getCategoryValue = (category: FilterCategoryItems, v: unknown): unknown => {
    const value =
        v === undefined || v === null ? '' : category.valueKey ? (v as Record<string, unknown>)[category.valueKey] : v;
    switch (category.dataType) {
        case FieldDefinitionFieldTypeNumeric.Date:
            return String(value).trim() === ''
                ? null
                : category.ignoreTime
                  ? String(value).split('T')[0]
                  : String(value);
        case FieldDefinitionFieldTypeNumeric.CSV:
            return String(value)
                .split(',')
                .map((item) => (item.trim() === '' || String(value).trim() === 'null' ? null : item.trim()));
        default:
            return String(value).trim() === '' || String(value).trim() === 'null' ? null : value;
    }
};

export const calculateFiltersFromTrackedChanges = (
    trackedChangesList: FilterUIStateUpdate[],
    categoryToExclude?: FilterCategoryItems,
    filterCategories?: FilterCategory[],
): FilterDescriptor[] => {
    const filters: FilterDescriptor[] = [];

    filterCategories?.forEach((filterCategory) => {
        filterCategory.items.forEach((category) => {
            if (!category.excludeBlank) return;
            if (
                categoryToExclude &&
                category.fieldKey === categoryToExclude.fieldKey &&
                category.valueKey === categoryToExclude.valueKey
            )
                return;

            const isApplied = trackedChangesList.some(
                (tc) => tc.fieldKey === category.fieldKey && tc.valueKey === category.valueKey,
            );
            if (!isApplied) return;

            const field = category.valueKey ? `${category.fieldKey}.${category.valueKey}` : category.fieldKey;
            filters.push({ field, operator: 'isnotnull' });
            filters.push({ field, operator: 'isnotempty' });
        });
    });

    trackedChangesList
        .filter((change) => change.dataType !== FieldDefinitionFieldTypeNumeric.CSV)
        .forEach((trackedChange) => {
            if (
                categoryToExclude &&
                trackedChange.fieldKey === categoryToExclude.fieldKey &&
                trackedChange.valueKey === categoryToExclude.valueKey
            )
                return;

            trackedChange.uncheckedValues.forEach((value) => {
                if (value === 'Blank' || value === null) {
                    if (trackedChange.valueKey) {
                        filters.push({
                            field: trackedChange.fieldKey,
                            operator: 'isnotnull',
                        });
                        filters.push({
                            field: trackedChange.fieldKey,
                            operator: 'isnotempty',
                        });
                    }
                    filters.push({
                        field: trackedChange.valueKey
                            ? `${trackedChange.fieldKey}.${trackedChange.valueKey}`
                            : trackedChange.fieldKey,
                        operator: 'isnotnull',
                    });
                    filters.push({
                        field: trackedChange.valueKey
                            ? `${trackedChange.fieldKey}.${trackedChange.valueKey}`
                            : trackedChange.fieldKey,
                        operator: 'isnotempty',
                    });
                } else if (trackedChange.dataType === FieldDefinitionFieldTypeNumeric.Date) {
                    filters.push({
                        field: trackedChange.valueKey
                            ? `${trackedChange.fieldKey}.${trackedChange.valueKey}`
                            : trackedChange.fieldKey,
                        operator: 'doesnotstartwith',
                        value,
                    });
                } else {
                    filters.push({
                        field: trackedChange.valueKey
                            ? `${trackedChange.fieldKey}.${trackedChange.valueKey}`
                            : trackedChange.fieldKey,
                        operator: 'neq',
                        value,
                    });
                }
            });
        });

    trackedChangesList
        .filter((change) => change.dataType === FieldDefinitionFieldTypeNumeric.CSV)
        .reduce((groups, item) => {
            const key = item.fieldKey;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item.uncheckedValues);
            return groups;
        }, new Map())
        .forEach((values, key) => {
            if (categoryToExclude && key === categoryToExclude.fieldKey) return;

            filters.push({
                field: key,
                operator: commaSplitCompare,
                value: values,
            });
            filters.push({
                field: key,
                operator: 'neq',
                value: Math.random(),
            });
        });
    return filters;
};

export const computeUncheckedValuesForExactMatch = (
    data: { [key: string]: unknown }[],
    category: FilterCategoryItems,
    matchValue: unknown,
): unknown[] => {
    const values = data.map((obj) => obj[category.fieldKey]).flatMap((v) => getCategoryValue(category, v));
    return [...new Set(values)].filter((value) => value !== matchValue);
};

export const buildExactMatchTrackedChange = (
    data: { [key: string]: unknown }[],
    category: FilterCategoryItems,
    matchValue: unknown,
    parentId: CategoryType,
): FilterUIStateUpdate => ({
    fieldKey: category.fieldKey,
    valueKey: category.valueKey,
    uncheckedValues: computeUncheckedValuesForExactMatch(data, category, matchValue),
    dataType: category.dataType,
    parentId,
});
